import { memo, useMemo } from 'react';
import { Form, Select } from 'semantic-ui-react';
import { COLUMN_TYPE } from '../../../../../../constants/generalConstants';
import {
    useChangeColumnMetadata,
    useColumnMetadata,
    useColumnsMetadata,
    useDefaultReportProps,
} from '../../../../../../stores/metadataStore';
import {
    ColContentType,
    ColIdType,
    FormatType,
    PeriodType,
    ReportPropsType,
    VersionType,
} from '../../../../../../types';
import { getOptions } from '../../../../../../utils/tableExtraction';
import { SUPPORTED_FIELD_VALUES } from '../../../../constants';
import './styles.css';
import { getSupportedPeriods } from './utils';

type ColumnMetadataFormPropsType = {
    colId: ColIdType;
};
const ColumnMetadataForm = memo(function ColumnMetadataForm({ colId }: ColumnMetadataFormPropsType) {
    const colMetadata = useColumnMetadata(colId);
    const colsMetadata = useColumnsMetadata();
    const changeColMetadata = useChangeColumnMetadata();
    const defaultReportProps = useDefaultReportProps();
    const reportProps = useMemo(() => {
        return colMetadata?.reportProps || defaultReportProps;
    }, [colMetadata]);

    /**
     * TODO: Discuss this approach. Maybe save the first initial report in store
     */
    const supportedPeriods = useMemo(() => {
        return getSupportedPeriods(colsMetadata);
    }, []);

    const handleReportPropsChange = (newReportPropsValues: Partial<ReportPropsType>) => {
        const newReportProps = { ...reportProps, ...newReportPropsValues };
        changeColMetadata(colId, { reportProps: newReportProps });
    };

    const handleChangeColContentType = (contentType: ColContentType) => {
        if (contentType === COLUMN_TYPE.REPORT && !colMetadata?.reportProps) {
            handleReportPropsChange(defaultReportProps);
        }
        changeColMetadata(colId, { contentType });
    };

    return (
        <Form className="column-metadata-form">
            <Form.Field>
                <Select
                    disabled={!colMetadata?.importCandidate}
                    placeholder={'Type'}
                    value={colMetadata?.contentType}
                    upward={false}
                    options={getOptions(SUPPORTED_FIELD_VALUES.COLUMN_TYPES)}
                    onChange={(__, data) => {
                        handleChangeColContentType(data.value as ColContentType);
                    }}
                />
            </Form.Field>
            {colMetadata?.contentType === COLUMN_TYPE.REPORT && (
                <>
                    <Form.Field>
                        <Select
                            disabled={!colMetadata?.importCandidate}
                            placeholder="Year"
                            value={reportProps.year}
                            upward={false}
                            options={getOptions(SUPPORTED_FIELD_VALUES.YEARS)}
                            onChange={(__, data) => handleReportPropsChange({ year: data.value as number })}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Select
                            disabled={!colMetadata?.importCandidate}
                            placeholder="Period"
                            upward={false}
                            value={reportProps.period}
                            options={getOptions(supportedPeriods)}
                            onChange={(__, data) => handleReportPropsChange({ period: data.value as PeriodType })}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Select
                            disabled={!colMetadata?.importCandidate}
                            placeholder="Version"
                            value={reportProps.version}
                            upward={false}
                            options={getOptions(SUPPORTED_FIELD_VALUES.VERSIONS)}
                            onChange={(__, data) => handleReportPropsChange({ version: data.value as VersionType })}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Select
                            disabled={!colMetadata?.importCandidate}
                            placeholder="Format"
                            value={reportProps.format}
                            upward={false}
                            options={getOptions(SUPPORTED_FIELD_VALUES.FORMATS)}
                            onChange={(__, data) => handleReportPropsChange({ format: data.value as FormatType })}
                        />
                    </Form.Field>
                </>
            )}
        </Form>
    );
});

export default ColumnMetadataForm;
